import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Accordion } from "../../faq/Accordian";
import { AppSeo } from "../../../components/app-seo/AppSeo";
import { Paths } from "../../../paths";
import "./SealFaqPage.scss";

export const SealFaqPage = () => {
  const navigate = useNavigate();

  useEffect(() => {
    const hash = window.location.hash;
    if (hash) {
      const element = document.querySelector(hash);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    }

    document.title = "OVR Seal — Frequently Asked Questions";

    return () => {
      document.title = "Old Vine Registry";
    };
  }, []);

  const aboutTheSeal = [
    {
      title: "1.1 What is the OVR Seal?",
      content:
        "The Old Vine Registry Seal identifies wines made with grapes from old-vine vineyards registered in the Old Vine Registry. It provides registered participants with a consistent way to communicate a wine's connection to a registered old-vine site.",
    },
    {
      title: "1.2 What does the seal tell consumers?",
      content:
        "The seal indicates that at least 85% of the wine's grapes come from one or more OVR-registered vineyards and that the qualifying grapes meet or exceed the age shown on the selected seal.",
    },
    {
      title: "1.3 Is the OVR Seal a certification?",
      content:
        "No. The seal is not a certification of wine quality, sustainability, farming practices, winemaking methods, regulatory compliance, or other product attributes. The registered participant is responsible for determining that each use meets the program requirements.",
    },
    {
      title:
        "1.4 Does OVR independently verify every wine that uses the seal?",
      content:
        "No. OVR registers participants rather than approving individual wines, vintages, labels, or website listings. During registration, the applicant attests that the registered participant will follow the eligibility and usage requirements. OVR reviews the vineyard information submitted with the registration before confirming participation.",
    },
    {
      title: "1.5 How is the seal connected to the Old Vine Registry?",
      content:
        "Every qualifying vineyard must have a public record in the Old Vine Registry. That record contains the vineyard's planting year, displayed age, location, and other available information. When a planting year is estimated, the record generally explains the basis for the estimate.",
    },
  ];

  const eligibilityAndAgeBadges = [
    {
      title: "2.1 Which wines qualify for the OVR Seal?",
      content:
        "A wine qualifies when at least 85% of its grapes come from one or more vineyards registered in the Old Vine Registry. The seal must be used only in connection with a qualifying wine.",
    },
    {
      title: "2.2 What happens to the remaining 15% of the wine?",
      content:
        "The remaining portion does not need to come from an OVR-registered vineyard. However, it cannot be counted toward the vineyard-source or age threshold shown on the seal.",
    },
    {
      title: "2.3 Which age badges are available?",
      content: (
        <>
          <ul>
            <li>35+ years</li>
            <li>50+ years</li>
            <li>75+ years</li>
            <li>100+ years</li>
            <li>150+ years</li>
          </ul>
        </>
      ),
    },
    {
      title: "2.4 How do I select the correct age badge?",
      content:
        "At least 85% of the wine's grapes must come from OVR-registered vineyards whose displayed ages meet or exceed the threshold shown on the badge. A wine should use the highest age badge for which at least 85% of its grapes qualify.",
    },
    {
      title:
        "2.5 How does the rule work when a wine comes from vineyards of different ages?",
      content:
        "Combine the percentages from all registered vineyards that meet a given age threshold. If those percentages total at least 85%, the wine may use that age badge. For example, a wine made with 60% fruit from a registered 100-year-old vineyard and 25% from a registered 75-year-old vineyard may use the 75+ seal. It may not use the 100+ seal because only 60% of the wine meets that threshold.",
    },
    {
      title:
        "2.6 Can a wine made from several registered vineyards qualify?",
      content:
        "Yes. A wine may qualify using grapes from one or more registered vineyards. The combined qualifying portion must meet the 85% vineyard-source and age requirements.",
    },
    {
      title: "2.7 Are estimated planting dates eligible?",
      content:
        "Yes. A vineyard's planting year may be exact or estimated. Both may be used to determine the appropriate badge. When the planting year is estimated, the vineyard's OVR record explains how the estimate was determined.",
    },
    {
      title: "2.8 How is vineyard age calculated for a vintage wine?",
      content:
        "Use the vineyard's planting year to determine its age in the vintage year when the grapes were harvested, rather than the year in which the wine is released or sold.",
    },
    {
      title: "2.9 Can a non-vintage wine use the seal?",
      content:
        "Yes, provided that at least 85% of the grapes contributing to the finished wine come from registered vineyards meeting or exceeding the age shown on the selected seal.",
    },
    {
      title:
        "2.10 Does registration of a vineyard property make every wine from that property eligible?",
      content:
        "Not necessarily. The grapes used in the wine must come from the registered vines or vineyard area represented by the OVR record. The presence of a registered vineyard somewhere on a larger property does not automatically make every wine associated with that property eligible.",
    },
  ];

  const whoMayRegister = [
    {
      title: "3.1 Who may register for the OVR Seal program?",
      content: (
        <>
          The program is open to:
          <ul>
            <li>Vineyard owners and operators</li>
            <li>Wine producers</li>
            <li>Wine brand owners</li>
            <li>Importers</li>
            <li>Retailers</li>
            <li>Certain old-vine membership organizations approved by OVR</li>
          </ul>
          <p>Distributors, wholesalers, agencies, consultants, and other third
          parties are not eligible unless they also qualify under one of the
          eligible roles above.</p><br/>
          <p className="description-text">
            <strong>South African Exception</strong>
          </p>
          <p className="description-text">
            The OVR Seal is not issued to or valid for use by South African vineyards or wine producers under any circumstances. This is because South Africa already has in place their <a href="https://www.oldvineproject.co.za/aboutus/certifiedheritagevineyardseal">Certified Heritage Vineyard</a> program developed by the <a href="https://www.oldvineproject.co.za">Old Vine Project</a>.  Interested producers from South Africa should engage with the <a href="https://www.oldvineproject.co.za">Old Vine Project</a> and utilize the CHV initiative in lieu of the OVR Seal.
          </p>
        </>
      ),
    },
    {
      title:
        "3.2 Does registration apply to a legal organization or to the wine brand?",
      content:
        "Registration applies to the named registered participant — the wine brand, vineyard business, importer, or retailer identified in the application — not to the legal organization as a whole. OVR does not separately register or approve every wine. Once confirmed, the registered participant may use the appropriate seal for its eligible wines and permitted applications, subject to the OVR Seal Usage Guidelines.",
    },
    {
      title:
        "3.3 Who must register to place the seal on a bottle or label?",
      content:
        "The consumer-facing wine brand must be registered directly with the OVR Seal program. Only a registered wine brand may place the seal on a bottle, label, closure, capsule, carton, case, or other wine packaging.",
    },
    {
      title:
        "3.4 Can an importer or retailer place the seal on a producer's bottles?",
      content:
        "No. A registered importer or retailer may use the seal in its own approved website, e-commerce, sales, or marketing contexts for an eligible wine. It may not provide the artwork to an unregistered wine brand for use on bottles or other packaging. That wine brand must register separately.",
    },
    {
      title:
        "3.5 Can a vineyard owner, operator, or wine brand owner register?",
      content:
        "Yes. Vineyard owners and operators and wine brand owners may register for uses appropriate to their roles, such as eligible website content, marketing materials, or signage. If the seal will appear on wine packaging, the wine brand must also be registered directly.",
    },
    {
      title:
        "3.6 What if the same company is a producer, brand owner, importer, or retailer?",
      content:
        "An applicant may select every eligible role that applies to the name being registered. Having multiple roles does not expand the registration to other wine brands or organizations. The authorization remains limited to the single named participant.",
    },
  ];

  const parentCompanies = [
    {
      title: "4.1 What does one OVR Seal registration cover?",
      content: (
        <>
          Each US $300 registration covers one named participant in the OVR Seal
          program. The named participant may be:
          <ul>
            <li>One consumer-facing wine brand</li>
            <li>One vineyard business</li>
            <li>One importing business</li>
            <li>One retail business</li>
            <li>One qualifying old-vine membership organization approved by OVR</li>
          </ul>
          One registration does not cover an entire portfolio of separately
          marketed wine brands. A registered wine brand may use the appropriate
          seal for all of its eligible wines and vintages — individual wines and
          vintages do not require separate registrations.
        </>
      ),
    },
    {
      title:
        "4.2 Our parent company owns several wine brands. Does one registration cover all of them?",
      content:
        "No. Each separately marketed consumer-facing wine brand that will use the OVR Seal must register separately and pay the US $300 one-time registration fee. Common ownership, production, management, importation, or corporate control does not combine several wine brands into one registration. Registration by a parent company does not authorize all brands in its portfolio to use the seal.",
    },
    {
      title:
        "4.3 How does registration work for a large wine company with many brands?",
      content:
        "Suppose one legal company owns 20 separately marketed wine brands, and five of those brands want to use the OVR Seal. The legal company may be listed as the organization responsible for registration and payment, but each of the five participating wine brands requires its own registration. The company would submit five registrations and pay five US $300 registration fees. Each registered brand may then use the appropriate seal for all of its eligible wines and vintages. The remaining 15 brands would not be authorized to use the seal.",
    },
    {
      title:
        "4.4 Does every wine, vineyard designation, or vintage require a separate registration?",
      content:
        "No. Registration applies to the consumer-facing wine brand, not to each individual wine or vintage. Different eligible wines, grape varieties, vineyard designations, cuv\u00e9es, and vintages sold under the same principal wine brand are generally covered by that brand's registration. A separately marketed brand with its own consumer-facing identity requires a separate registration, even if it shares the same parent company or producer. OVR may determine whether a name represents a separate wine brand based on how it is labeled, marketed, and presented to consumers.",
    },
    {
      title:
        "4.5 Can a parent company register under its corporate name?",
      content:
        "A parent company may appear as the legal organization responsible for a registration. However, if the seal will be used by a consumer-facing wine brand, the registration must identify that specific brand. Registering a corporate or holding-company name does not create portfolio-wide rights for its subsidiary or affiliated brands. A parent company may use the seal under its own name only in a context specifically approved by OVR, and only when the presentation does not imply that every brand or wine in its portfolio qualifies.",
    },
  ];

  const tradeOrganizations = [
    {
      title:
        "5.1 Can a trade association or regional wine marketing body register?",
      content: (
        <>
          Generally, no. Regional wine marketing bodies, generic promotional
          organizations, trade associations, appellation organizations, and
          similar membership bodies are not ordinarily eligible to register. An
          exception may be available when the organization's membership criteria
          require members to own, farm, or produce wine from vines that are at
          least 35 years old. Eligibility is not established merely because an
          organization promotes a region containing old vineyards or has some
          members who produce old-vine wine — the old-vine requirement must be a
          defined condition of membership. OVR must review and approve any
          organization seeking to register under this exception and the specific
          terms of that registration will be collaboratively determined. OVR is
          particularly interested in such organizations that may want to register
          all of their members in a single batch and require the usage of the OVR
          Seal by those members after such a registration.{" "}
          <a href="mailto:oldvineregistry@gmail.com" className="seal-faq-link">
            Please reach out to discuss such efforts.
          </a>
        </>
      ),
    },
  ];

  const packagingAndPermittedUses = [
    {
      title:
        "6.1 Who must register for the seal to appear on wine packaging?",
      content:
        "The consumer-facing wine brand shown on the packaging must be registered. The application must be submitted by the wine producer, wine brand owner, or another eligible organization authorized to control packaging for that brand. Registration of a parent company, vineyard business, importer, retailer, old-vine membership organization, or another wine brand does not authorize packaging use by an unregistered brand.",
    },
    {
      title:
        "6.2 Can an importer or retailer provide the artwork to a producer?",
      content:
        "No. An importer or retailer may use the seal in its own authorized communications for eligible wines, but it may not provide the artwork to a producer or wine brand. The wine brand must register separately before the seal may appear on its packaging.",
    },
    {
      title: "6.3 How may importers and retailers use the seal?",
      content:
        "Registered importers and retailers may display the appropriate seal beside eligible wines on their own websites, e-commerce listings, catalogs, wine lists, shelf talkers, retail displays, and promotional materials. The registration applies only to communications issued by the registered importer or retailer. It does not register the wine brands they carry, authorize packaging use, or permit them to distribute the artwork to another organization.",
    },
    {
      title: "6.4 How may a registered vineyard business use the seal?",
      content:
        "A registered vineyard owner or operator may use the appropriate seal in connection with its registered vineyard, including on its website, promotional materials, and vineyard signage. Registration of the vineyard business does not authorize an unregistered wine brand to use the seal on wine packaging. Vineyard signage must not imply that every block on the property meets the displayed age or that every wine associated with the vineyard qualifies.",
    },
    {
      title: "6.5 Where else may the seal be used?",
      content:
        "Depending on the registered participant's role, permitted uses may include wine packaging, websites, e-commerce listings, printed and digital sales materials, shelf talkers, wine lists, trade presentations, advertisements, social media, event materials, and vineyard or winery signage. Packaging use is limited to a registered wine brand.",
    },
    {
      title:
        "6.6 How should the seal appear on a website or e-commerce listing?",
      content:
        "Place the seal close to the qualifying wine's name, image, or description. It should remain legible on desktop and mobile screens and should link to the relevant OVR vineyard record or records whenever practical. Do not place the seal in a global website header, footer, or navigation area if that placement could imply that every wine on the site qualifies.",
    },
    {
      title:
        "6.7 How should the seal be used on a wine list or display containing several wines?",
      content:
        "Place the seal directly beside each qualifying wine or use another clear system that lets readers identify exactly which wines qualify. Do not place one seal above a group of wines if some of those wines are ineligible.",
    },
    {
      title: "6.8 May I alter the artwork to match my label or website?",
      content:
        "No. Use only official artwork supplied by OVR. Do not redraw, stretch, rotate, recolor, crop, rearrange, add effects to, or combine the seal with another logo. Use an approved artwork version that provides sufficient contrast and meets the minimum-size and clear-space requirements.",
    },
    {
      title: "6.9 What wording may accompany the seal?",
      content: (
        <>
          Recommended language includes: "This wine is made with grapes from
          vineyards registered in the Old Vine Registry" or "Made with at least
          85% of its grapes from Old Vine Registry vineyards aged [age] years or
          more." Do not describe a wine, vineyard, or participant as certified,
          approved, guaranteed, authenticated, or endorsed by OVR, and do not
          imply that OVR independently verified a wine's composition, quality,
          provenance, production methods, or regulatory compliance.
        </>
      ),
    },
    {
      title:
        "6.10 Must a digital use of the seal link to the vineyard's OVR page?",
      content:
        "A direct link is recommended whenever practical because it lets consumers explore the vineyard information behind the seal. Printed materials may use the vineyard's URL or a QR code linking to its OVR record.",
    },
  ];

  const registrationFeesAndTiming = [
    {
      title: "7.1 How much does registration cost?",
      content:
        "The registration fee is US $300 for each named wine brand, vineyard business, importing business, retail business, or approved old-vine membership organization. There are no annual renewal fees. Additional wine brands require separate registrations.",
    },
    {
      title: "7.2 Does a large company receive a volume discount?",
      content:
        "OVR does not currently offer automatic portfolio or volume pricing. Each separately marketed wine brand requires its own registration and fee. If OVR introduces a formal multi-brand pricing policy in the future, it will apply consistently and will not change the requirement that every participating brand be separately identified and registered.",
    },
    {
      title: "7.3 Is there an annual renewal fee?",
      content:
        "No. There is no annual renewal fee under the current program. Registered participants remain responsible for following the current Usage Guidelines and reassessing the eligibility of each wine and vintage.",
    },
    {
      title: "7.4 What information is needed to register?",
      content: (
        <>
          <ul>
            <li>Applicant and legal organization contact information</li>
            <li>The name being registered and its role or roles</li>
            <li>Intended seal uses</li>
            <li>At least one complete OVR vineyard page URL</li>
            <li>Authority to register on behalf of the named participant</li>
            <li>Agreement to the program attestations and Usage Guidelines</li>
            <li>Payment of the one-time registration fee</li>
          </ul>
        </>
      ),
    },
    {
      title: "7.5 How long does registration take?",
      content:
        "Registration and vineyard review may take 1\u20133 weeks. OVR may contact the applicant if additional vineyard or registration information is required.",
    },
    {
      title: "7.6 May I begin using the seal after submitting payment?",
      content:
        "No. Submission and payment do not authorize immediate use. The registered participant may begin using the OVR Seal only after OVR confirms the registration and provides the official artwork and current Usage Guidelines.",
    },
    {
      title: "7.7 What happens during the review?",
      content:
        "OVR reviews the registration and submitted vineyard information. It may also seek regional input as an additional indication of the underlying vineyard record's credibility. This review does not constitute independent verification or product-level approval of individual wines.",
    },
    {
      title: "7.8 What will I receive after confirmation?",
      content: (
        <>
          <ul>
            <li>Official seal artwork appropriate to the registered participant's permitted uses</li>
            <li>Available age-badge files</li>
            <li>The current OVR Seal Usage Guidelines</li>
            <li>Confirmation that the named participant is an authorized program participant</li>
          </ul>
        </>
      ),
    },
    {
      title: "7.9 What if the vineyard I need is not in the OVR?",
      content:
        "The vineyard must be submitted to and listed in the Old Vine Registry before it can support use of the seal. Use the Submit Vineyard feature on the OVR website, then complete the seal registration after the vineyard record is available.",
    },
  ];

  const artworkTransfersAndAffiliatedOrganizations = [
    {
      title: "8.1 May registered participants share the artwork?",
      content:
        "No. Official artwork may not be given, sold, sublicensed, distributed, or otherwise provided to another wine brand or organization for its independent use. A participant may provide artwork to a printer, designer, web developer, or other service provider working solely on its behalf. That service provider receives no independent right to retain, reuse, or distribute it.",
    },
    {
      title: "8.2 Are affiliated companies covered?",
      content:
        "No. Parent companies, subsidiaries, affiliated brands, and other organizations under common ownership are not automatically covered. Each separately marketed wine brand requires its own registration. Each vineyard business, importer, retailer, or approved old-vine membership organization must also register under its own name.",
    },
  ];

  const changesQuestionsAndMisuse = [
    {
      title: "9.1 When should eligibility be reassessed?",
      content:
        "Reassess a wine whenever its vineyard sources or percentages change, a new vintage is produced, a non-vintage blend changes, or a relevant vineyard record is corrected, revised, or removed from the OVR.",
    },
    {
      title:
        "9.2 What happens if a vineyard record changes after packaging has been printed?",
      content:
        "Contact OVR to discuss the circumstances. OVR recognizes that finished packaging already in circulation may not always be immediately recoverable. However, the registered participant should not continue applying the seal to new packaging or marketing materials once it knows that the wine no longer qualifies.",
    },
    {
      title:
        "9.3 What happens if OVR believes the seal is being misused?",
      content:
        "OVR may ask the registered participant to explain how a wine qualifies, identify the relevant vineyard records, correct an inaccurate badge or statement, remove the seal from controllable digital materials, or stop applying it to new packaging, and may establish a reasonable correction plan for existing materials. OVR may withdraw its recognition of the named participant and remove it from any public authorized-participant listing when use is materially inaccurate, misleading, unauthorized, or inconsistent with the guidelines. Suspension or termination applies to the named participant and does not transfer authorization to a parent company, affiliated brand, or other organization.",
    },
    {
      title: "9.4 How can possible misuse be reported?",
      content: (
        <>
          Email{" "}
          <a
            href="mailto:oldvineregistry@gmail.com"
            className="seal-faq-link"
          >
            oldvineregistry@gmail.com
          </a>{" "}
          with the participant or wine involved, a photograph, screenshot, or
          link showing the use, the location or market where it appeared, and a
          short explanation of the concern. OVR may contact the participant
          involved for clarification.
        </>
      ),
    },
    {
      title:
        "9.5 Where can I ask for help with eligibility, artwork, or a proposed use?",
      content: (
        <>
          Contact{" "}
          <a
            href="mailto:oldvineregistry@gmail.com"
            className="seal-faq-link"
          >
            oldvineregistry@gmail.com
          </a>
          . Include the name being registered, the wine and vintage if
          applicable, links to the relevant OVR vineyard records, the age badge
          you intend to use, and a description or image of the proposed
          application.
        </>
      ),
    },
  ];

  return (
    <>
      <AppSeo
        title="OVR Seal — Frequently Asked Questions"
        keywords="OVR Seal, old vine seal, old vine registry seal, FAQ, frequently asked questions"
      />
      <div className="about-layout">
        <div>
          <a
            href={Paths.seal}
            className="seal-faq-back-link"
            onClick={(e) => {
              e.preventDefault();
              navigate(Paths.seal);
            }}
          >
            &larr; Back to the OVR Seal
          </a>

          <h1 className="title-large">
            OVR Seal — Frequently Asked Questions
          </h1>

          <p className="seal-faq-subtitle">
            A public reference for organizations and individuals interested in
            participating in the Old Vine Registry Seal program.
          </p>

          <div className="seal-faq-info-box">
            <div className="seal-faq-info-box-title">Quick answer</div>
            <div className="seal-faq-info-box-body">
              The OVR Seal identifies wines made with at least 85% of their
              grapes from old-vine vineyards registered in the Old Vine
              Registry. Each registration covers one named participant — one
              wine brand, vineyard business, importer, or retailer — and only a
              registered wine brand may place the seal on packaging.
            </div>
          </div>

          <div className="seal-faq-table-of-contents">
            <a href="#faq-about">About the OVR Seal</a>
            <a href="#faq-eligibility">Wine Eligibility and Age Badges</a>
            <a href="#faq-whomust">Who May Register</a>
            <a href="#faq-parent">
              Parent Companies and Multiple Wine Brands
            </a>
            <a href="#faq-membership">Trade Organizations</a>
            <a href="#faq-using">Packaging and Permitted Uses</a>
            <a href="#faq-registration">
              Registration, Fees, and Review Timing
            </a>
            <a href="#faq-artwork">
              Artwork, Transfers, and Affiliated Organizations
            </a>
            <a href="#faq-misuse">
              Changes, Questions, and Possible Misuse
            </a>
          </div>

          <div id="faq-about" className="seal-faq-section">
            <h2 className="sub-headline">1. About the OVR Seal</h2>
            <Accordion items={aboutTheSeal} />
          </div>

          <div id="faq-eligibility" className="seal-faq-section">
            <h2 className="sub-headline">
              2. Wine Eligibility and Age Badges
            </h2>
            <Accordion items={eligibilityAndAgeBadges} />
          </div>

          <div id="faq-whomust" className="seal-faq-section">
            <h2 className="sub-headline">3. Who May Register</h2>
            <Accordion items={whoMayRegister} />
          </div>

          <div id="faq-parent" className="seal-faq-section">
            <h2 className="sub-headline">
              4. Parent Companies and Multiple Wine Brands
            </h2>
            <Accordion items={parentCompanies} />
          </div>

          <div id="faq-membership" className="seal-faq-section">
            <h2 className="sub-headline">5. Trade Organizations</h2>
            <Accordion items={tradeOrganizations} />
          </div>

          <div id="faq-using" className="seal-faq-section">
            <h2 className="sub-headline">6. Packaging and Permitted Uses</h2>
            <Accordion items={packagingAndPermittedUses} />
          </div>

          <div id="faq-registration" className="seal-faq-section">
            <h2 className="sub-headline">
              7. Registration, Fees, and Review Timing
            </h2>
            <Accordion items={registrationFeesAndTiming} />
          </div>

          <div id="faq-artwork" className="seal-faq-section">
            <h2 className="sub-headline">
              8. Artwork, Transfers, and Affiliated Organizations
            </h2>
            <Accordion items={artworkTransfersAndAffiliatedOrganizations} />
          </div>

          <div id="faq-misuse" className="seal-faq-section">
            <h2 className="sub-headline">
              9. Changes, Questions, and Possible Misuse
            </h2>
            <Accordion items={changesQuestionsAndMisuse} />
          </div>

          <p className="seal-faq-footer-note">
            These FAQs summarize the OVR Seal program. If there is a conflict
            between this document and the current{" "}
            <a
              href={Paths.sealGuidelines}
              className="seal-faq-link"
              onClick={(e) => {
                e.preventDefault();
                navigate(Paths.sealGuidelines);
              }}
            >
              OVR Seal Usage Guidelines
            </a>
            , the Usage Guidelines govern.
          </p>
        </div>
      </div>
    </>
  );
};
